import React, { FC, useEffect, useMemo, useState } from 'react';
import * as echarts from 'echarts';
import ReactECharts from 'echarts-for-react';
import { useTheme } from 'styled-components';
import { useTranslate } from '@/translations';
import {
  TRACKING_MAP,
  TRACKING_MAP_H,
  TRACKING_MAP_W,
  TRACKING_ROUTE,
  TRACKING_SITE,
  TRACKING_VIN,
} from '../fixtures/trackingMap';
import { ChartCard } from './ChartCard';
import { SampleDataBadge } from './WidgetStates';

const HEIGHT = 460;
const MAP_NAME = 'thorne-compound';
const ROUTE_COLOR = '#1677ff';
const PENDING_COLOR = '#f5871f';

export const TrackingMap: FC = () => {
  const { t } = useTranslate();
  const { accent, primaryBg, textColor } = useTheme();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (typeof fetch !== 'function') return undefined;
    let cancelled = false;
    fetch(TRACKING_MAP)
      .then((r) => r.blob())
      .then(
        (blob) =>
          new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          }),
      )
      .then((dataUrl) => {
        if (cancelled) return;
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${TRACKING_MAP_W} ${TRACKING_MAP_H}"><image href="${dataUrl}" width="${TRACKING_MAP_W}" height="${TRACKING_MAP_H}"/></svg>`;
        echarts.registerMap(MAP_NAME, { svg });
        setReady(true);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const done = TRACKING_ROUTE.filter((s) => s.done);
  const current = done[done.length - 1];
  const next = TRACKING_ROUTE.find((s) => !s.done);

  const option: echarts.EChartsOption = useMemo(
    () => ({
      tooltip: { trigger: 'item', formatter: '{b}' },
      geo: { map: MAP_NAME, roam: true, zoom: 2 },
      series: [
        {
          type: 'lines',
          coordinateSystem: 'geo',
          polyline: true,
          lineStyle: { color: ROUTE_COLOR, width: 4 },
          data: [{ coords: done.map((s) => [s.x, s.y]) }],
        },
        ...(current && next
          ? [
              {
                type: 'lines' as const,
                coordinateSystem: 'geo' as const,
                polyline: true,
                lineStyle: {
                  color: PENDING_COLOR,
                  width: 4,
                  type: 'dashed' as const,
                },
                data: [
                  {
                    coords: [
                      [current.x, current.y],
                      [next.x, next.y],
                    ],
                  },
                ],
              },
            ]
          : []),
        {
          type: 'scatter',
          coordinateSystem: 'geo',
          symbolSize: 22,
          z: 5,
          data: TRACKING_ROUTE.map((s, i) => ({
            value: [s.x, s.y],
            name: s.zone,
            itemStyle: {
              color: s.done ? ROUTE_COLOR : '#ffffff',
              borderColor: s.done ? '#ffffff' : PENDING_COLOR,
              borderWidth: 2,
            },
            label: {
              show: true,
              formatter: String(i + 1),
              color: s.done ? '#ffffff' : PENDING_COLOR,
              fontWeight: 700,
              fontSize: 12,
            },
          })),
        },
      ],
    }),
    [done, current, next],
  );

  return (
    <ChartCard
      title={t('dashboard.widget.trackingMap')}
      extra={<SampleDataBadge />}
      height={HEIGHT}
    >
      <div style={{ display: 'flex', gap: 16, height: HEIGHT }}>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            height: HEIGHT,
            background: '#f5f7fa',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          {ready ? (
            <ReactECharts
              option={option}
              style={{ height: '100%', width: '100%' }}
              notMerge
            />
          ) : null}
        </div>

        <div style={{ width: 340, flexShrink: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: primaryBg }}>
            {TRACKING_VIN}
          </div>
          <div
            style={{
              fontSize: 12,
              color: textColor,
              opacity: 0.6,
              marginBottom: 12,
            }}
          >
            {TRACKING_SITE} · {done.length}/{TRACKING_ROUTE.length}{' '}
            {t('dashboard.trackingMap.zones')}
          </div>

          <div>
            {TRACKING_ROUTE.map((s, i) => {
              const isCurrent = current && s.zone === current.zone;
              return (
                <div
                  key={s.zone}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '5px 8px',
                    borderRadius: 6,
                    background: isCurrent ? '#eaf3ff' : 'transparent',
                  }}
                >
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      flexShrink: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                      color: s.done ? '#ffffff' : PENDING_COLOR,
                      background: s.done ? ROUTE_COLOR : '#ffffff',
                      border: `2px solid ${s.done ? ROUTE_COLOR : PENDING_COLOR}`,
                    }}
                  >
                    {i + 1}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      color: s.done ? textColor : '#9aa0a6',
                      fontWeight: isCurrent ? 600 : 400,
                      fontSize: 13,
                    }}
                  >
                    {s.zone}
                    {isCurrent ? (
                      <span
                        style={{ color: accent, fontSize: 11, marginLeft: 6 }}
                      >
                        {t('dashboard.trackingMap.current')}
                      </span>
                    ) : null}
                  </span>
                  <span
                    style={{
                      color: s.done ? accent : '#bdc3c7',
                      fontSize: 12,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {s.time}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </ChartCard>
  );
};
